import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useHotel } from "../../contexts/HotelContext";
import TenantSwitcher from "./TenantSwitcher";
import {
  ChevronLeftIcon,
  SettingsIcon,
  UsersIcon,
  LogoutIcon,
  MenuIcon,
} from "../icons";

import { User } from "../../contexts/HotelContext";

interface TopBarProps {
  user: User | null;
  showBackButton?: boolean;
  onToggleSidebar: () => void;
}

const TopBar: React.FC<TopBarProps> = ({
  user,
  showBackButton = false,
  onToggleSidebar,
}) => {
  const { hasModuleEnabled, getTenantDisplayInfo, selectedTenantId } = useHotel();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem(`de-minimis-form-data-v1-${selectedTenantId || "default"}`);
    navigate("/login");
  };

  const handleBackClick = () => {
    navigate("/");
  };

  return (
    <header className="bg-white shadow-sm border-b border-neutral-200 h-16 flex items-center justify-between px-6">
      {/* Left Section */}
      <div className="flex items-center space-x-4">
        {/* Mobile Sidebar Toggle */}
        <button
          onClick={onToggleSidebar}
          data-tour="mobile-menu-btn"
          aria-label="Otwórz menu"
          className="lg:hidden p-2 text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
        >
          <MenuIcon className="h-5 w-5" />
        </button>

        {/* Back Button */}
        {showBackButton && (
          <button
            onClick={handleBackClick}
            className="p-2 text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors lg:hidden"
            title="Wróć do panelu"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
        )}

        {/* Hotel Name (visible on smaller screens) */}
        <div className="hidden sm:block lg:hidden">
          <h2 className="text-lg font-medium text-neutral-700">
            {getTenantDisplayInfo().title}
          </h2>
        </div>
      </div>

      {/* Right Section - Tenant Switcher & User Menu */}
      <div className="flex items-center space-x-4">
        {/* Tenant Switcher (hidden for single-tenant users automatically) */}
        <TenantSwitcher />

        {/* User Menu */}
        <div className="relative">
          <div className="flex items-center space-x-3">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-neutral-900">
                {user?.firstName || "User"}
              </p>
              {hasModuleEnabled("hotel:offers") && (
                <p className="text-xs text-neutral-500">Menedżer hotelu</p>
              )}
            </div>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center hover:bg-primary-200 transition-colors"
            >
              <span className="text-sm font-medium text-primary-700">
                {user?.firstName?.charAt(0) || "U"}
              </span>
            </button>
          </div>

          {showUserMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-neutral-200 py-1 z-50">
              <Link
                to="/account/settings"
                className="block px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
                onClick={() => setShowUserMenu(false)}
              >
                <div className="flex items-center">
                  <SettingsIcon className="h-4 w-4 mr-2" />
                  Ustawienia konta
                </div>
              </Link>
              {user?.role === "admin" && hasModuleEnabled("hotel:manage") && (
                <Link
                  to="/hotel-settings"
                  className="block px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
                  onClick={() => setShowUserMenu(false)}
                >
                  <div className="flex items-center">
                    <UsersIcon className="h-4 w-4 mr-2" />
                    Ustawienia hotelu
                  </div>
                </Link>
              )}
              <button
                onClick={handleLogout}
                className="block w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
              >
                <div className="flex items-center">
                  <LogoutIcon className="h-4 w-4 mr-2" />
                  Wyloguj
                </div>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default TopBar;
